/**
 * @module builder
 */

import type { PresentationBlock, PresentationBlockOption, PresentationOptionV2 } from 'paintscout'
import { uuid } from '../../../util'
import { findBlock, findSection, insertAt, moveWithin, removeAt, replaceAt, withPages } from './locate'

/**
 * CRUD over v2 **blocks** — a single piece of content (text, image, gallery, the estimate itself,
 * …) inside a section. This is the level the renderers under PresentationNext/blocks/ draw.
 */

/**
 * `label` is deliberately blanked rather than copied from the catalog entry.
 *
 * The catalog needs a `label` (it's what the block picker lists), but on a placed block a copied name
 * is a name frozen at creation: it goes stale when the type is renamed. Empty means "no name of its
 * own", so the editor shows the block's *type*, resolved live from the catalog by
 * `getPresentationSectionLabel`; renaming a block row in the sidebar tree fills `label` in as an
 * override.
 *
 * `title` is NOT blanked — it's body content (five types render it as an `h2`: mission, awards,
 * services, documents, about-estimator), so its default belongs to the catalog entry per type.
 */
export function createPresentationNextBlock(args: { blockOption: PresentationBlockOption }): PresentationBlock {
  const { blockOption } = args

  return {
    ...blockOption,
    label: '',
    key: uuid()
  }
}

export function addPresentationNextBlock(args: {
  presentation: PresentationOptionV2
  sectionKey: string
  block: PresentationBlock
  /** Defaults to appending to the section. */
  index?: number
}): PresentationOptionV2 {
  const { presentation, sectionKey, block, index } = args
  const location = findSection(presentation, sectionKey)

  if (!location) {
    return presentation
  }

  const { page, pageIndex, section, sectionIndex } = location
  const nextSection = {
    ...section,
    blocks: insertAt(section.blocks ?? [], index ?? Number.MAX_SAFE_INTEGER, block)
  }
  const nextPage = { ...page, sections: replaceAt(page.sections, sectionIndex, nextSection) }

  return withPages(presentation, replaceAt(presentation.pages, pageIndex, nextPage))
}

export function updatePresentationNextBlock(args: {
  presentation: PresentationOptionV2
  block: PresentationBlock
}): PresentationOptionV2 {
  const { presentation, block } = args
  const location = findBlock(presentation, block.key)

  if (!location) {
    return presentation
  }

  const { page, pageIndex, section, sectionIndex, blockIndex } = location
  const nextSection = { ...section, blocks: replaceAt(section.blocks, blockIndex, block) }
  const nextPage = { ...page, sections: replaceAt(page.sections, sectionIndex, nextSection) }

  return withPages(presentation, replaceAt(presentation.pages, pageIndex, nextPage))
}

export function deletePresentationNextBlock(args: {
  presentation: PresentationOptionV2
  blockKey: string
}): PresentationOptionV2 {
  const { presentation, blockKey } = args
  const location = findBlock(presentation, blockKey)

  if (!location) {
    return presentation
  }

  const { page, pageIndex, section, sectionIndex, blockIndex } = location
  const nextSection = { ...section, blocks: removeAt(section.blocks, blockIndex) }
  const nextPage = { ...page, sections: replaceAt(page.sections, sectionIndex, nextSection) }

  return withPages(presentation, replaceAt(presentation.pages, pageIndex, nextPage))
}

/** Duplicates a block directly below the original, with a fresh key. */
export function copyPresentationNextBlock(args: {
  presentation: PresentationOptionV2
  blockKey: string
}): PresentationOptionV2 {
  const { presentation, blockKey } = args
  const location = findBlock(presentation, blockKey)

  if (!location) {
    return presentation
  }

  const { page, pageIndex, section, sectionIndex, block, blockIndex } = location
  const nextSection = {
    ...section,
    blocks: insertAt(section.blocks, blockIndex + 1, { ...block, key: uuid() })
  }
  const nextPage = { ...page, sections: replaceAt(page.sections, sectionIndex, nextSection) }

  return withPages(presentation, replaceAt(presentation.pages, pageIndex, nextPage))
}

export function reorderPresentationNextBlocks(args: {
  presentation: PresentationOptionV2
  sectionKey: string
  fromIndex: number
  toIndex: number
}): PresentationOptionV2 {
  const { presentation, sectionKey, fromIndex, toIndex } = args
  const location = findSection(presentation, sectionKey)

  if (!location) {
    return presentation
  }

  const { page, pageIndex, section, sectionIndex } = location
  const blocks = section.blocks ?? []

  if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= blocks.length) {
    return presentation
  }

  const nextSection = { ...section, blocks: moveWithin(blocks, fromIndex, toIndex) }
  const nextPage = { ...page, sections: replaceAt(page.sections, sectionIndex, nextSection) }

  return withPages(presentation, replaceAt(presentation.pages, pageIndex, nextPage))
}

/**
 * Moves a block to another section — the same section (reorder), another section on the same page,
 * or a section on a different page. Drag-and-drop in the editor funnels through here.
 */
export function movePresentationNextBlock(args: {
  presentation: PresentationOptionV2
  blockKey: string
  toSectionKey: string
  /** Defaults to appending to the destination section. */
  toIndex?: number
}): PresentationOptionV2 {
  const { presentation, blockKey, toSectionKey, toIndex } = args
  const from = findBlock(presentation, blockKey)
  const to = findSection(presentation, toSectionKey)

  if (!from || !to) {
    return presentation
  }

  if (from.section.key === toSectionKey) {
    return reorderPresentationNextBlocks({
      presentation,
      sectionKey: toSectionKey,
      fromIndex: from.blockIndex,
      toIndex: toIndex ?? from.section.blocks.length - 1
    })
  }

  const withBlockRemoved = deletePresentationNextBlock({ presentation, blockKey })

  return addPresentationNextBlock({
    presentation: withBlockRemoved,
    sectionKey: toSectionKey,
    block: from.block,
    index: toIndex
  })
}
